import React, { useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import IconButton from '@cotyorg/ccx-molecules/IconButton';
import { Cardo } from '@cotyorg/ccx-organism-cardo';
import R from 'ramda';

const generateWrapperClassNames = ({ extraClass }) => (
  classNames('cardo-list', {
    [extraClass]: extraClass,
  })
);

const generateGridContainerClassNames = ({ fullWidth }) => (
  classNames('grid-container', {
    full: fullWidth,
  })
);

// TODO: [2] consider using virtualised list/intersection observer sentinel to improve performance of long lists
const CardoList = ({
  data = [],
  cardoList, // cardoLists
  showMore,
  hideShowMore,
  extraClass,
  fullWidth,
  showMoreText,
  showMoreIconSrc,
  showMoreIconSize = 'small',
  showMoreIconWidth,
  showMoreIconHeight,
  initialNumberToShow = 12,
  // id should be supplied, but this prevents the change being breaking and works for single Cardo List instances
  id = 'default-cardo-list-id',
  initialiseCardoList,
  onShowMoreClick,
  ...cardoProps
}) => {
  useEffect(() => {
    if (!R.prop(id, cardoList)) {
      // Add to redux if not already
      initialiseCardoList({ id });
    }
  }, []);

  const showMoreOnClick = useCallback(
    () => {
      showMore({ id });
      hideShowMore({ id });

      if (onShowMoreClick) {
        onShowMoreClick({ id });
      }
    },
    [id],
  );

  const isOpen = !!R.path([id, 'isOpen'], cardoList);
  const showMoreIsHidden = R.path([id, 'showMoreIsHidden'], cardoList);

  return (
    <div className={generateWrapperClassNames({ extraClass })}>
      <div className={generateGridContainerClassNames({ fullWidth })}>
        <div className="grid-x grid-padding-x">
          {
            data.slice(0, initialNumberToShow).map(({ id: key, ...item }, index) => (
              <Cardo
                data={item}
                key={key}
                index={index}
                extraClass="cardo-list__cardo-body"
                {...cardoProps}
              />
            ))
          }
        </div>
      </div>
      {data.length > initialNumberToShow && isOpen && (
        <div className={generateGridContainerClassNames({ fullWidth })}>
          <div className="grid-x grid-padding-x">
            {
              data.slice(initialNumberToShow).map(({ id: key, ...item }, index) => (
                <Cardo
                  data={item}
                  key={key}
                  index={index + initialNumberToShow}
                  extraClass="cardo-list__cardo-body"
                  {...cardoProps}
                />
              ))
            }
          </div>
        </div>
      )}
      {!showMoreIsHidden && data.length > initialNumberToShow && (
        <div className="grid-x align-center">
          <IconButton
            iconSrc={showMoreIconSrc}
            size={showMoreIconSize}
            iconWidth={showMoreIconWidth}
            iconHeight={showMoreIconHeight}
            extraClass="cardo-list__show-more-button"
            textExtraClass="cardo-list__show-more-button-text"
            onClick={showMoreOnClick}
          >
            {showMoreText}
          </IconButton>
        </div>
      )}
    </div>
  );
};

CardoList.propTypes = {
  data: PropTypes.arrayOf(PropTypes.object),
  showMore: PropTypes.func.isRequired,
  hideShowMore: PropTypes.func.isRequired,
  extraClass: PropTypes.string,
  fullWidth: PropTypes.string,
  showMoreText: PropTypes.string,
  showMoreIconSrc: PropTypes.string,
  showMoreIconSize: PropTypes.string,
  showMoreIconWidth: PropTypes.number,
  showMoreIconHeight: PropTypes.number,
  initialNumberToShow: PropTypes.number,
  id: PropTypes.string.isRequired,
  cardoList: PropTypes.arrayOf(PropTypes.object),
  initialiseCardoList: PropTypes.func.isRequired,
};

export default CardoList;
